import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const path = location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    return {
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        currentPageUrl,
    };
};

interface OwnProps {
    carouselType: string;
    product: CartLineModel | ProductContextModel | OrderLineModel;
    extendedStyles?: ProductDescriptionStyles;
    index?: number;
    carouselTitle: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        css: css`
            width: 100%;

            @media print {
                display: block !important;
            }
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
};

const ProductDescription: FC<Props> = ({ carouselType, product, extendedStyles, category, currentPageUrl, index, carouselTitle }) => {
    const [styles] = React.useState(() => mergeToNew(productDescriptionStyles, extendedStyles));

    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");

    const sku = "product" in product ? product.product.productNumber : "erpNumber" in product ? product.erpNumber : "";

    const position = "product" in product ? product.idx! + 1 : "line" in product ? product.line : 0;

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    const condition = "product" in product ? product.product.properties.condition : "line" in product ? product.properties.cartLineCondition : "lineNumber" in product ? product.properties.condition : "";
    
    const productClickDataLayerEvent = () => {
        let listName = "";
        if (carouselTitle !== undefined && category?.shortDescription !== undefined) {
            listName = carouselTitle;
        } else if (carouselType !== undefined && category?.shortDescription === undefined) {
            listName = carouselTitle;
        } else if (carouselTitle === undefined && category?.shortDescription !== undefined) {
            listName = "Category Results";
        }

        listName = currentPageUrl.includes("Search")
            ? "Search Results"
            : currentPageUrl.includes("Cart")
                ? "Cart Lines"
                : currentPageUrl.includes("category/All/")
                    ? "Brand Results"
                    : currentPageUrl.includes("category/all")
                        ? "Brand Results"
                        : currentPageUrl.includes("SavedOrders")
                            ? "Saved Order Details Page"
                            : currentPageUrl.includes("Orders/Details")
                                ? "Order History Details"
                                : currentPageUrl.includes("MyLists")
                                    ? "Product Wish List"
                                    : currentPageUrl.includes("CheckoutReviewAndSubmit")
                                        ? "Order Review"
                                        : currentPageUrl.includes("OrderConfirmation")
                                            ? "Order Confirmation"
                                            : listName;


        const dataLayer = (window as any).dataLayer || [];
        // dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.    

        dataLayer.push({
            event: "productClick",
            ecommerce: {
                click: {
                    actionField: { list: listName, "action": "click" }, // Optional list property.                    
                    products: [
                        {
                            name: description, // Name or ID is required.
                            // id: "product" in product ? product.product.productNumber : "",
                            id: sku,
                            price:
                                "pricing" in product
                                    ? product.pricing?.actualPrice
                                    : "product" in product
                                        ? product.productInfo.pricing?.actualPrice
                                        : product.unitNetPrice,

                            brand: "product" in product ? product.product.brand?.name : product.brand?.name,
                            condition,
                            category: category?.shortDescription ?? "",
                            stock:
                                "product" in product
                                    ? (
                                        //Condition to show stock as "DirectShip" when total qty<=0 and isDirectShipFromManufacturer is true
                                        (product.productInfo?.inventory?.qtyOnHand!=undefined && product.productInfo?.inventory?.qtyOnHand<=0 
                                            && product.product.properties.isDirectShipFromManufacturer.toLowerCase()=="true")
                                        ? "DirectShip" : 
                                        (product.productInfo?.inventory?.inventoryAvailabilityDtos?.find(
                                            o =>
                                                o.unitOfMeasure.toLowerCase() ===
                                                (product.productInfo?.unitOfMeasure?.toLowerCase() || ""),
                                        )?.availability || undefined
                                        )?.message
                                        )
                                    : "",
                            stockqty: "product" in product ? product.productInfo.inventory?.qtyOnHand : 0,
                            // position: "product" in product ? product.idx! + 1 : 0,
                            position: String(index) !== "undefined" ? Number(index) + 1 : position,
                        },
                    ],
                },
            },
        });
    };
    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <Link
            {...styles.productDetailLink}
            href={productDetailPath}
            data-test-selector="productDescriptionLink"
            onClick={productClickDataLayerEvent}
            className="product-detail-link"
        >
            {description}
        </Link>
    ) : (
        <Typography {...styles.productDetailDescription}>{description}</Typography>
    );
};

export default connect(mapStateToProps)(ProductDescription);